import { Platform } from 'react-native';

export interface GrowthVisitorMetadata {
    /** Google Ads click id from `gclid` or the `_gcl_aw` cookie. Credits the server conversion to the click. */
    gclid?: string;
    /** Google Ads iOS click id from `gbraid` or the `_gcl_gb` cookie. Used when the click has no gclid. */
    gbraid?: string;
    /** Google Ads web-to-app click id from `wbraid`. Used when the click has no gclid or gbraid. */
    wbraid?: string;
    /** GA4 client id from the `_ga` cookie. Sent as `client_id` on the server event. */
    gaClientId?: string;
    /** Meta browser id from the `_fbp` cookie. Sent on the server Meta event. */
    fbp?: string;
    /** Meta click id from the `_fbc` cookie, or built from `fbclid`. Sent on the server Meta event. */
    fbc?: string;
}

const readCookie = (name: string): string | undefined => {
    if (typeof document === 'undefined') {
        return undefined;
    }
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
        const separator = cookie.indexOf('=');
        if (separator === -1) {
            continue;
        }
        const cookieName = cookie.slice(0, separator).trim();
        if (cookieName !== name) {
            continue;
        }
        const value = decodeURIComponent(cookie.slice(separator + 1).trim());
        return value === '' ? undefined : value;
    }
    return undefined;
};

const queryParam = (name: string): string | undefined => {
    if (typeof window === 'undefined') {
        return undefined;
    }
    const value = new URLSearchParams(window.location.search).get(name);
    return value != null && value !== '' ? value : undefined;
};

/** `GCL.<timestamp>.<value>` stores the value. The value itself may contain dots. */
export const clickIdFromGclCookie = (
    cookie: string | undefined,
): string | undefined => {
    if (cookie == null || cookie === '') {
        return undefined;
    }
    const parts = cookie.split('.');
    if (parts.length < 3 || parts[0] !== 'GCL') {
        return undefined;
    }
    const value = parts.slice(2).join('.');
    return value === '' ? undefined : value;
};

/** `GA1.1.123.456` stores `123.456`. */
export const gaClientIdFromCookie = (
    cookie: string | undefined,
): string | undefined => {
    if (cookie == null || cookie === '') {
        return undefined;
    }
    const match = /^GA\d+\.\d+\.(.+)$/.exec(cookie);
    const value = match?.[1];
    return value != null && value !== '' ? value : undefined;
};

export const fbcFromClick = (
    fbclid: string | undefined,
    nowMs: number,
): string | undefined => {
    if (fbclid == null || fbclid === '') {
        return undefined;
    }
    return `fb.1.${nowMs}.${fbclid}`;
};

const readWebAdIds = (nowMs: number): GrowthVisitorMetadata => {
    const fbclid = queryParam('fbclid');
    const metadata: GrowthVisitorMetadata = {
        gclid:
            queryParam('gclid') ?? clickIdFromGclCookie(readCookie('_gcl_aw')),
        gbraid:
            queryParam('gbraid') ?? clickIdFromGclCookie(readCookie('_gcl_gb')),
        wbraid: queryParam('wbraid'),
        gaClientId: gaClientIdFromCookie(readCookie('_ga')),
        fbp: readCookie('_fbp'),
        fbc: readCookie('_fbc') ?? fbcFromClick(fbclid, nowMs),
    };
    return metadata;
};

const omitEmpty = (metadata: GrowthVisitorMetadata): GrowthVisitorMetadata => {
    const result: GrowthVisitorMetadata = {};
    for (const [key, value] of Object.entries(metadata)) {
        if (typeof value === 'string' && value.trim() !== '') {
            result[key as keyof GrowthVisitorMetadata] = value.trim();
        }
    }
    return result;
};

/** Google and Meta ids that exist before a member is created. */
export const collectGrowthVisitorMetadata = (): GrowthVisitorMetadata => {
    const webIds: GrowthVisitorMetadata =
        Platform.OS === 'web' ? readWebAdIds(Date.now()) : {};

    return omitEmpty(webIds);
};
